import type { Pinia } from 'pinia';
import type { Context } from './@types/ssr';

import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createPinia } from 'pinia';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import Cookies from 'js-cookie';

/** Pinia Persisted State Plugin namespace à la jw/user. */
export const STORE_NAMESPACE = 'jw';

export async function createPiniaStore(context: Partial<Context>) {
	const pinia = createPinia();
	pinia.use(piniaPluginPersistedstate);

	if (process.server && context.req?.cookies) {
		server_createPiniaStore(pinia, context.req.cookies);
	}

	if (process.client) {
		await client_createPiniaStore(pinia);
	}

	return pinia;
}

function server_createPiniaStore(pinia: Pinia, cookies: NonNullable<Context['req']>['cookies']) {
	const savedState: any = {};

	const userCookie = cookies[`${STORE_NAMESPACE}_user`] || cookies[`${STORE_NAMESPACE}/user`];
	if (userCookie) {
		try {
			savedState.user = JSON.parse(userCookie);

			// migrate settings to the new splitted properties settingsProvider , settingsJWConsents , settingsUCConsents
			if (savedState.user?.settings) {
				const { settings } = savedState.user;
				savedState.user.settingsProvider = settings.settingsProvider || savedState.user.settingsProvider;
				savedState.user.jw_consents = settings.jw_consents || savedState.user.jw_consents;
				savedState.user.uc_consents = settings.uc_consents || savedState.user.uc_consents;
			}
		} catch {
			console.error(`[piniaStore.ts] createStore error parsing ${STORE_NAMESPACE}_user`, userCookie);
		}
	}

	const token = cookies['access_token'];

	if (token) {
		(savedState.user ??= {}).accessToken = token;
	}

	if (savedState.user) {
		useUserStore(pinia).$patch(savedState.user);
	}

	const filterCookie = cookies[`${STORE_NAMESPACE}_filter`] || cookies[`${STORE_NAMESPACE}/filter`];
	if (filterCookie) {
		try {
			const savedFilter = JSON.parse(filterCookie);
			const filterStore = useFilterStore(pinia);

			// savedFilter is keyed by webLocale e.g. { 'de': { popular: {...}, new: {...} } }
			// We need to merge each webLocale's filter collections into the store state
			// rather than $patch directly, since state is a reactive({}) not a plain object
			for (const [webLocale, countryFilters] of Object.entries(savedFilter)) {
				if (!filterStore.getWebLocale(webLocale as any)) {
					filterStore.initFiltersPerLocale(webLocale as any);
				}
				// merge each collection type's filters into the initialized locale
				if (countryFilters && typeof countryFilters === 'object') {
					for (const [collectionType, filterCollection] of Object.entries(countryFilters)) {
						filterStore.setFilters(webLocale as any, collectionType as any, filterCollection as any);
					}
				}
			}
		} catch {
			console.error(`[piniaStore.ts] createStore error parsing ${STORE_NAMESPACE}_filter`, filterCookie);
		}
	}
}

async function client_createPiniaStore(pinia: Pinia) {
	// Cookies are the source of truth for auth state across all domains
	const accessTokenCookie = Cookies.get('access_token');

	const userCookie = Cookies.get(`${STORE_NAMESPACE}_user`) || Cookies.get(`${STORE_NAMESPACE}/user`);
	let parsedCookieUser = null;
	try {
		parsedCookieUser = JSON.parse(userCookie ?? 'null');
	} catch (err) {
		captureMessageForSentry(
			'[piniaStore.ts] Error parsing user cookie',
			{ err, where: 'piniaStore.ts client_createPiniaStore (cookie parsing)' },
			'error'
		);
	}

	const cookieHasAuth = parsedCookieUser != null && parsedCookieUser.jwLoginId != null && accessTokenCookie != null;

	// localStorage is not cross domain, changing auth state in domain then moving
	// to the other will leave local storage auth info out of sync with the cookie.
	let savedUser: Record<string, any> = {};
	try {
		savedUser = JSON.parse(localStorage.getItem(`${STORE_NAMESPACE}/user`) ?? 'null') ?? {};
	} catch (err) {
		captureMessageForSentry(
			'[piniaStore.ts] Error parsing saved user from localStorage',
			{ err, where: 'piniaStore.ts client_createPiniaStore (localStorage parsing)' },
			'error'
		);
	}

	const localStorageHasAuth = savedUser.jwLoginId != null && savedUser.accessToken != null;

	// XSS-safe deserialization of uneval result:
	// https://github.com/Rich-Harris/devalue?tab=readme-ov-file#xss-mitigation
	const serverSentState = (0, eval)('(' + (window?.__DATA__ as any)?.piniaState + ')') ?? {};

	// Snapshot window.__DATA__.piniaState.user immediately before anything can modify it
	if (serverSentState?.user) {
		// Deep clone just the user state to preserve original SSR state
		(window as any).__INITIAL_SSR_USER__ = serverSentState?.user ?? {};
		// check if jw_user exist in document.cookie
		(window as any).__INITIAL_USER_COOKIE_AVAILABILITY__ = !!document.cookie
			.split('; ')
			.some(cookie => cookie.startsWith('jw_user='));
		(window as any).__INITIAL_USER_LOCALSTORAGE_AVAILABILITY__ = !!localStorage.getItem('jw/user');
	}

	if (cookieHasAuth !== localStorageHasAuth) {
		// Cookie says logged in, localStorage says logged out
		// 	→ Apply login state to localStorage from Cookie
		if (cookieHasAuth) {
			savedUser = mergeCookieAuthIntoLocalStorageUser(parsedCookieUser, accessTokenCookie, savedUser);
		}

		// Cookie says logged out, localStorage says logged in
		// 	→ Apply logout state to localStorage
		if (localStorageHasAuth) {
			savedUser = clearAuthStateFromLocalStorage(savedUser, parsedCookieUser);
		}

		localStorage.setItem(`${STORE_NAMESPACE}/user`, JSON.stringify(savedUser));

		// Check if server auth matches cookie auth
		const serverStateHasAuth = serverSentState.user?.jwLoginId != null && serverSentState.user?.accessToken != null;

		// Ignore sever state if auth states don't match (server stale)
		// If they match, baseState is fresh and we should use it for settings
		if (cookieHasAuth !== serverStateHasAuth) {
			serverSentState.user = {};
		}
	}

	const serverSentUser =
		Object.keys(serverSentState.user || {}).length === 0
			? {}
			: {
					settings: serverSentState?.user?.settings,
					preferences: serverSentState?.user?.preferences,
					buyboxExpView: serverSentState?.user?.buyboxExpView,
					newBuyboxView: serverSentState?.user?.newBuyboxView,
					newBuyboxFilterType: serverSentState?.user?.newBuyboxFilterType,
			  };

	// SAVE INITIAL USER STATE;
	(window as any).__INITIAL_SAVED_USER_STATE__ = JSON.parse(JSON.stringify(savedUser));

	serverSentState.user = {
		...serverSentState.user,
		...savedUser,
		...serverSentUser,
	};

	pinia.state.value = serverSentState;
}

function mergeCookieAuthIntoLocalStorageUser(cookieUser: any, accessTokenCookie: string, localStorageUser: any) {
	// Start with cookie data as base (has all necessary fields including jwId)
	// Then merge in localStorage preferences to preserve user customizations
	// Note: Only include properties that are actually persisted to cookies (see persist-state.plugin.ts)
	const authFieldsFromCookie = {
		jwId: cookieUser.jwId,
		jwLoginId: cookieUser.jwLoginId,
		accessToken: accessTokenCookie,
		loggedInProviders: cookieUser.loggedInProviders ?? [],
		isPremium: cookieUser.isPremium,
		userRole: cookieUser.userRole,
		onboardingStatus: cookieUser.onboardingStatus,
		lastUsedLoginProvider: cookieUser.lastUsedLoginProvider,
		accountCreationTime: cookieUser.accountCreationTime,
	};

	// Merge: cookie as base, then layer localStorage on top, then auth fields override
	return {
		// Base: all fields from cookie
		...cookieUser,

		// Override: preferences from localStorage
		...localStorageUser,

		// Final override: auth fields from cookie
		...authFieldsFromCookie,
	};
}

// Preserve existing user object, only clear auth-related fields
function clearAuthStateFromLocalStorage(localStorageUser: any, cookieUser: any) {
	return {
		...localStorageUser,
		jwLoginId: undefined,
		accessToken: undefined,
		loggedInProviders: [],
		isPremium: false,
		userRole: 'USER',
		info: {},
		profileInfo: null,
		onboardingStatus: 'GLOBAL',
		lastUsedLoginProvider: cookieUser?.lastUsedLoginProvider || '',
		accountCreationTime: null,
		profileIsComplete: false,
	};
}
